import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Loader2, FileDown, Pencil, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";

// Page d'approbation de l'ÉPREUVE (demande du propriétaire, 2026-10-06) — lien
// sécurisé du courriel « Votre épreuve est prête », AUCUN compte requis.
// Le client accepte (pilule dorée dans l'admin) ou demande des modifications
// avec un commentaire (pilule rouge jusqu'à une nouvelle épreuve).
export default function Epreuve() {
  const { token } = useParams();
  const { lang } = useLang();
  const en = lang === "en";
  const [data, setData] = useState(null);
  const [invalide, setInvalide] = useState(false);
  const [askChanges, setAskChanges] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [avertOk, setAvertOk] = useState(false);  // avertissement variations de gravure — obligatoire avant d'accepter
  const [done, setDone] = useState("");  // "acceptee" | "modifications" (réponse à l'instant)

  useEffect(() => {
    api.get(`/quote-proof/${token}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setInvalide(true));
  }, [token]);

  const status = done || data?.status || "";
  const fileSrc = data?.file_url ? `${process.env.REACT_APP_BACKEND_URL}${data.file_url}` : "";
  const isImg = /\.(png|jpe?g|webp|gif|svg)$/i.test(data?.file_name || "");
  const driveDl = data?.drive_download || data?.drive_link || "";  // épreuve hébergée sur Google Drive (lien direct)

  const accept = async () => {
    if (!avertOk) return;  // l'avertissement doit être coché (le bouton est grisé sinon)
    setBusy(true);
    try {
      await api.post(`/quote-proof/${token}/accept`, { warning_accepted: true });
      setDone("acceptee");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendChanges = async () => {
    if (comment.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/quote-proof/${token}/changes`, { comment: comment.trim() });
      setDone("modifications");
      setAskChanges(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="epreuve-page">
      <SEO title={en ? "Your proof — Mr Trophée" : "Votre épreuve — Mr Trophée"} path={`/epreuve/${token}`} noindex />
      <div className="max-w-2xl mx-auto px-6">
        {invalide ? (
          <FadeUp>
            <div className="bg-surface border border-red-400/30 rounded-2xl p-8 text-center" data-testid="epreuve-invalide">
              <h1 className="font-serif text-3xl mb-3">{en ? "Invalid link" : "Lien invalide"}</h1>
              <p className="text-sm text-ivory/60 leading-relaxed">
                {en
                  ? "This proof link is no longer valid. Write to us at info@trophee.ca and we will gladly send it again."
                  : "Ce lien d'épreuve n'est plus valide. Écrivez-nous à info@trophee.ca et nous vous le renverrons avec plaisir."}
              </p>
              <Link to="/" className="inline-block mt-6 text-gold hover:underline text-sm" data-testid="epreuve-home">
                {en ? "Back to the home page" : "Retour à l'accueil"}
              </Link>
            </div>
          </FadeUp>
        ) : !data ? (
          <p className="flex items-center justify-center gap-2 text-ivory/50 py-24" data-testid="epreuve-loading">
            <Loader2 className="w-5 h-5 animate-spin text-gold" /> {en ? "Loading…" : "Chargement…"}
          </p>
        ) : (
          <FadeUp>
            <p className="text-gold tracking-[0.3em] uppercase text-xs mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> {en ? "Secure approval" : "Approbation sécurisée"}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl mb-3" data-testid="epreuve-title">
              {en ? "Your proof is ready" : "Votre épreuve est prête"}
            </h1>
            <p className="text-sm text-ivory/60 mb-8">
              {en
                ? `Hello ${data.name} — check every detail (texts, logo, layout), then answer below. No production starts without your approval.`
                : `Bonjour ${data.name} — vérifiez chaque détail (textes, logo, disposition), puis répondez ci-dessous. Aucune production ne démarre sans votre accord.`}
            </p>

            {/* Bandeau d'état */}
            {status === "acceptee" && (
              <div className="mb-6 rounded-xl border border-gold/50 bg-gold/10 p-5" data-testid="epreuve-acceptee-banner">
                <p className="text-gold font-semibold text-sm flex items-center gap-2">
                  <Check className="w-4 h-4" /> {en ? "Proof approved — thank you!" : "Épreuve acceptée — merci !"}
                </p>
                <p className="text-xs text-ivory/60 mt-1">
                  {en ? "Your order now goes into production." : "Votre commande passe maintenant en production."}
                </p>
              </div>
            )}
            {status === "modifications" && (
              <div className="mb-6 rounded-xl border border-red-400/40 bg-red-400/5 p-5" data-testid="epreuve-changes-banner">
                <p className="text-red-300 font-semibold text-sm">
                  {en ? "Changes requested — sent to the workshop" : "Modifications demandées — transmises à l'atelier"}
                </p>
                {(done ? comment : data.client_comment) && (
                  <p className="text-xs text-ivory/70 mt-1.5 border-l-2 border-red-400/50 pl-2" data-testid="epreuve-comment-echo">
                    « {done ? comment : data.client_comment} »
                  </p>
                )}
                <p className="text-xs text-ivory/60 mt-1.5">
                  {en
                    ? "We will send you a new proof shortly — you will answer it right here, with this same link."
                    : "Nous vous enverrons une nouvelle épreuve sous peu — vous y répondrez ici même, avec ce même lien."}
                </p>
              </div>
            )}

            {/* L'épreuve */}
            <div className="bg-surface border border-gold/20 rounded-2xl p-5 sm:p-7 mb-6" data-testid="epreuve-file-card">
              {(data.items || []).length > 0 && (
                <p className="text-[11px] uppercase tracking-widest text-ivory/50 mb-3">
                  {en ? "This proof covers :" : "Cette épreuve concerne :"}
                  <span className="block mt-1 text-ivory/75 normal-case tracking-normal text-xs">
                    {data.items.map((it) => `${it.product_name} ×${it.qty}`).join(" · ")}
                  </span>
                </p>
              )}
              {fileSrc && (isImg ? (
                <a href={fileSrc} target="_blank" rel="noreferrer" data-testid="epreuve-file-zoom" title={en ? "Open full size" : "Ouvrir en grand"}>
                  <img src={fileSrc} alt={en ? "Your proof" : "Votre épreuve"} data-testid="epreuve-file-img"
                    className="w-full rounded-xl border border-ivory/10 bg-ink object-contain max-h-[60vh] hover:border-gold/50 transition-colors duration-300" />
                </a>
              ) : (
                <a href={fileSrc} target="_blank" rel="noreferrer" download
                  data-testid="epreuve-file-download"
                  className="flex items-center justify-center gap-2.5 rounded-xl border border-gold/30 bg-ink px-6 py-8 text-gold text-sm hover:bg-gold/10 transition-colors duration-300">
                  <FileDown className="w-5 h-5" /> {en ? `Download the proof (${data.file_name})` : `Télécharger l'épreuve (${data.file_name})`}
                </a>
              ))}
              {/* Épreuve hébergée sur GOOGLE DRIVE (demande du propriétaire,
                  2026-07-02) : bouton de téléchargement direct — aucun compte
                  Google requis côté client */}
              {driveDl && (
                <a href={driveDl} target="_blank" rel="noreferrer"
                  data-testid="epreuve-drive-download"
                  className={`flex items-center justify-center gap-2.5 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-6 py-8 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/20 transition-colors duration-300 ${fileSrc ? "mt-3" : ""}`}>
                  <FileDown className="w-5 h-5" /> {en ? "Download the proof (Google Drive)" : "Télécharger l'épreuve (Google Drive)"}
                </a>
              )}
            </div>

            {/* Avertissement OBLIGATOIRE avant acceptation (demande du propriétaire
                2026-10-10) : le client lit et coche — consigné avec date côté serveur */}
            {status !== "acceptee" && (
              <div className="mb-6 rounded-xl border border-amber-400/40 bg-amber-400/5 p-5 sm:p-6"
                data-testid="epreuve-avertissement">
                <p className="text-amber-300 font-semibold text-xs uppercase tracking-widest mb-2.5">
                  {en ? "Warning" : "Avertissement"}
                </p>
                <p className="text-xs text-ivory/75 leading-relaxed mb-4" data-testid="epreuve-avertissement-texte">
                  {en
                    ? "The textual and graphic preview is provided for reference only. Slight variations in appearance (depth, contrast or alignment) may occur during actual engraving due to the material's characteristics and texture. As each piece is unique, the final result may differ slightly from the screen while preserving the spirit of your design. By approving your layout via the link received by email, you accept these possible variations inherent to the engraving process."
                    : "L'aperçu textuel et graphique est fourni à titre indicatif. De légères variations de rendu (profondeur, contraste ou alignement) peuvent survenir lors de la gravure réelle en raison des caractéristiques et de la texture du matériau. Chaque pièce étant unique, le résultat final peut légèrement différer de l'écran tout en conservant l'esprit de votre design. En validant votre montage via le lien reçu par courriel, vous acceptez ces éventuelles variations inhérentes au processus de gravure."}
                </p>
                <label className="flex items-start gap-3 cursor-pointer select-none"
                  data-testid="epreuve-avertissement-label" htmlFor="epreuve-avertissement-check">
                  <input id="epreuve-avertissement-check" type="checkbox"
                    checked={avertOk} onChange={(e) => setAvertOk(e.target.checked)}
                    data-testid="epreuve-avertissement-check"
                    className="mt-0.5 w-4 h-4 shrink-0 accent-[#d4af37]" />
                  <span className="text-sm text-ivory/90 font-medium">
                    {en ? "I have read and I accept this warning *" : "J'ai lu et j'accepte cet avertissement *"}
                  </span>
                </label>
              </div>
            )}

            {/* Réponses */}
            {status !== "acceptee" && !askChanges && (
              <div className="flex flex-col sm:flex-row gap-3" data-testid="epreuve-actions">
                <button onClick={accept} disabled={busy || !avertOk} data-testid="epreuve-accept"
                  title={!avertOk ? (en ? "Please read and check the warning first" : "Lisez et cochez l'avertissement d'abord") : ""}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-4 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {en ? "I approve the proof" : "J'accepte l'épreuve"}
                </button>
                <button onClick={() => setAskChanges(true)} disabled={busy} data-testid="epreuve-ask-changes"
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-ivory/25 text-ivory/80 px-6 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-60">
                  <Pencil className="w-4 h-4" />
                  {en ? "Request changes" : "Demander des modifications"}
                </button>
              </div>
            )}
            {status !== "acceptee" && askChanges && (
              <div className="bg-surface border border-gold/20 rounded-2xl p-5 sm:p-6" data-testid="epreuve-changes-form">
                <label htmlFor="epreuve-comment" className="block text-xs uppercase tracking-widest text-ivory/60 mb-2">
                  {en ? "Describe the changes you want *" : "Décrivez les modifications souhaitées *"}
                </label>
                <textarea id="epreuve-comment" rows={4} maxLength={2000} value={comment}
                  onChange={(e) => setComment(e.target.value)} data-testid="epreuve-comment"
                  placeholder={en ? "e.g. Please center the logo and change the date to 2027…" : "ex. Centrer le logo et changer la date pour 2027…"}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300 mb-4" />
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={sendChanges} disabled={busy || comment.trim().length < 3} data-testid="epreuve-send-changes"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500/90 text-white font-bold px-6 py-3.5 rounded-full hover:bg-red-500 transition-colors duration-300 disabled:opacity-60">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {en ? "Send my changes" : "Envoyer mes modifications"}
                  </button>
                  <button onClick={() => setAskChanges(false)} disabled={busy} data-testid="epreuve-cancel-changes"
                    className="text-xs text-ivory/55 hover:text-gold transition-colors duration-300 px-4">
                    {en ? "Cancel" : "Annuler"}
                  </button>
                </div>
              </div>
            )}

            <p className="text-[11px] text-ivory/40 mt-8 text-center">
              {en
                ? "A question? Reply to our email or write to info@trophee.ca — +1 514-565-6665."
                : "Une question ? Répondez à notre courriel ou écrivez à info@trophee.ca — +1 514-565-6665."}
            </p>
          </FadeUp>
        )}
      </div>
    </div>
  );
}
